// src/pages/PatientSubscriptionPage.jsx
// Route: /patient/subscription
//
// Enhancement #3, 30 Jul 2026: domestic (non-Dubai) patients get their own
// plan. Deliberately built as a patient-facing page (not routed through the
// Subscriber/beneficiary system) — this is a patient paying for themselves,
// not an NRI paying for someone else. See PatientPlanController.java's Javadoc
// for the backend side of this distinction.
//
// 12 Aug 2026: the single ₹250 BASIC tier became three (V78) — Record Vault,
// Navigator, Family. The tiers and their prices are read from
// GET /plan/catalog rather than hardcoded here, so a price change is a server
// env var edit and this page cannot quote a number the patient won't be
// charged.
//
// No Figma frame exists for this screen — the card-per-tier layout follows the
// same shape SubscriberPlanPage already uses for its own tier choice.

import { useState, useEffect, useCallback } from 'react';
import { usePatient } from '../context/PatientContext';
import { PatientAppShell } from '../components/PatientLayout';
import WalletBillingTabs from '../components/WalletBillingTabs';
import { patientPlanAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { openRazorpayCheckout } from '../services/razorpay';

async function handleDownloadPlanInvoice(patientId, toast, setDownloading) {
  if (!patientId) return;
  setDownloading(true);
  try {
    const res = await patientPlanAPI.downloadInvoice(patientId);
    const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
    if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
  } catch (err) {
    toast.error(err.response?.data?.message || 'Could not generate an invoice for your plan.');
  } finally {
    setDownloading(false);
  }
}

function isStillActive(patient) {
  if (patient?.subscriptionStatus !== 'ACTIVE' || !patient?.subscriptionRenewalAt) return false;
  return new Date(patient.subscriptionRenewalAt) > new Date();
}

function formatInr(amountMinorUnits) {
  return `₹${(amountMinorUnits / 100).toLocaleString('en-IN')}`;
}

export default function PatientSubscriptionPage() {
  const { patient, profile, plan, refreshPatient, refreshProfile } = usePatient();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(null); // the plan code being bought
  const [error, setError] = useState('');
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  const patientId = patient?.id;
  const active = isStillActive(patient);
  // The tier the server says this patient is on — the same answer the sidebar
  // chip and the navigator portal render.
  const currentPlanCode = profile?.planCode || null;

  const loadCatalog = useCallback(() => {
    if (!patientId) return;
    setLoadingCatalog(true);
    patientPlanAPI.catalog(patientId)
      .then(res => setCatalog(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCatalog([]))
      .finally(() => setLoadingCatalog(false));
  }, [patientId]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  async function handleSubscribe(option) {
    if (submitting || !patientId) return;
    setSubmitting(option.plan); setError('');
    try {
      const orderRes = await patientPlanAPI.createOrder(patientId, option.plan);
      const order = orderRes.data;

      const response = await openRazorpayCheckout({
        amount: order.amountMinorUnits,
        currency: order.currency,
        name: 'Previu Health',
        description: `Previu ${option.label}`,
        orderId: order.gatewayOrderId,
        prefill: {
          name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          contact: patient.phone,
        },
        themeColor: 'var(--text-primary)',
      });
      if (!response) { setSubmitting(null); return; } // user dismissed checkout

      await patientPlanAPI.verifyOrder(patientId, {
        plan: option.plan,
        gatewayOrderId: response.razorpay_order_id,
        gatewayPaymentId: response.razorpay_payment_id,
        gatewaySignature: response.razorpay_signature,
      });

      // refreshPatient() only re-reads localStorage — it doesn't re-fetch
      // from the API — so update the cached patient object first, same
      // pattern SubscriberCheckoutPage.jsx already uses for this exact
      // "refresh() doesn't refetch" gotcha.
      const renewalAt = new Date();
      renewalAt.setFullYear(renewalAt.getFullYear() + 1);
      const updated = {
        ...patient,
        subscriptionStatus: 'ACTIVE',
        subscriptionPlan: option.plan,
        subscriptionRenewalAt: renewalAt.toISOString(),
      };
      localStorage.setItem('previu_patient', JSON.stringify(updated));
      refreshPatient?.();
      // Also re-read the server record, which is what the sidebar plan chip
      // and the notification bell derive from — without this they keep
      // showing the old tier until the next page load.
      refreshProfile?.();
      loadCatalog();

      toast.success(`Your ${option.label} is active!`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not complete your subscription.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Wallet & Billing', to: '/patient/wallet' }, { label: 'Subscription' }]}>
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        <WalletBillingTabs />
        <h1 className="page-title">My Plan</h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>
          Billed in INR, once a year.
        </p>

        {plan?.coveredBySubscriber ? (
          /* A patient enrolled by a subscriber is already covered under that
             subscriber's plan. PatientPlanController exists for a patient
             paying for themselves and is a deliberately separate system from
             the Subscriber one (see its Javadoc) — nothing reconciles a double
             purchase, so selling a plan here would just take money for cover
             the patient already has. */
          <div className="card" style={{ borderLeft: '3px solid var(--green)' }}>
            <div className="card-title" style={{ marginBottom: 8 }}>
              {plan.guardianDubai ? 'Covered under Guardian Dubai' : 'Covered by your subscriber'}
            </div>
            <div className="field-text">
              Your Previu membership is paid for
              {plan.subscriberName ? <> by <strong>{plan.subscriberName}</strong></> : ' by your subscriber'}.
              There's nothing for you to buy — and any change to the plan is made
              from their account.
            </div>
          </div>
        ) : (
          <>
            {active && (
              <div className="alert" style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {profile?.planLabel || 'Your plan'} — Active
                </div>
                <div style={{ fontSize: 13 }}>
                  Renews on {new Date(patient.subscriptionRenewalAt).toLocaleDateString(undefined, {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDownloadPlanInvoice(patientId, toast, setDownloadingInvoice)}
                  disabled={downloadingInvoice}
                  style={{ marginTop: 12 }}
                >
                  {downloadingInvoice ? 'Generating…' : '⬇ Download Invoice'}
                </button>
              </div>
            )}

            {error && (
              <div role="alert" style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {loadingCatalog ? (
              <div className="skeleton" style={{ height: 120 }} />
            ) : catalog.length === 0 ? (
              <div className="empty-state">
                <div className="empty-title">Plans unavailable</div>
                <div className="empty-desc">We couldn't load the plans just now. Please try again shortly.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                {catalog.map((option) => {
                  const isCurrent = option.plan === currentPlanCode;
                  return (
                    <div
                      key={option.plan}
                      className="card"
                      style={isCurrent ? { borderLeft: '3px solid var(--green)' } : undefined}
                    >
                      <div className="card-row">
                        <div>
                          <div className="card-title">
                            {option.label}
                            {isCurrent && <span className="badge badge-active" style={{ marginLeft: 8 }}>Current</span>}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                            {formatInr(option.amountMinorUnits)} per year
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleSubscribe(option)}
                          disabled={Boolean(submitting) || isCurrent}
                        >
                          {submitting === option.plan
                            ? 'Please wait…'
                            : isCurrent ? 'Your plan' : `Choose — ${formatInr(option.amountMinorUnits)}`}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Downgrades are refused server-side while a paid year is running
                (PatientPlanChangeRules), so say so here rather than letting the
                patient find out at the 409. */}
            {active && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14 }}>
                You can move up a tier at any time. Moving down is available once your
                current year ends — talk to your Navigator if you need it sooner.
              </p>
            )}
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
